import React, { useEffect, useMemo, useRef, useState } from "react";
import SplashHeader from "./SplashHeader";
import getSplashData from "components/utility/api/GetSplashData";
import AudienceSortModal from "components/public-site/audience-sort/AudienceSortModal";
import ProfessionalAffirmation from "components/public-site/audience-sort/ProfessionalAffirmation";
import CountrySelector from "components/public-site/audience-sort/CountrySelector";
import AudienceSelector from "components/public-site/audience-sort/AudienceSelector";
import { audienceSortStepNames } from "components/public-site/audience-sort/utility/audienceSortStepNames";
import AudienceSelectorMobile from "components/public-site/audience-sort/AudienceSelectorMobile";
import CountrySelectorMobile from "components/public-site/audience-sort/CountrySelectorMobile";
import ProfessionalAffirmationMobile from "components/public-site/audience-sort/ProfessionalAffirmationMobile";
import AudienceSelectorGlobalSplash from "components/public-site/audience-sort/AudienceSelectorGlobalSplash";
import { changeCountry } from "components/public-site/audience-sort/utility/audienceSortApiHandlers";
import { SCREEN_WIDTHS } from "components/utility/constants/screen_widths";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { LocationChangeCTA } from "components/public-site/audience-sort/LocationChangeCTA";

const GlobalSplashPage = () => {
	const audienceSortTheme = "light-theme-teal";
	const currentPage = "global-splash";

	const navbarRef = useRef(null);

	const [isMobile, setIsMobile] = useState(window.innerWidth <= SCREEN_WIDTHS.SM);
	const [isMobileXS, setIsMobileXS] = useState(window.innerWidth <= SCREEN_WIDTHS.XS);
	const [initialData, setInitialData] = useState(null);
	const [countryListData, setCountryListData] = useState(null);
	const [audienceTypesData, setAudienceTypesData] = useState(null);
	const [audienceSelectionData, setAudienceSelectionData] = useState(null);
	const [step, setStep] = useState(audienceSortStepNames.initialStep);
	const [cameFromInitialStep, setCameFromInitialStep] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const backgroundImage = useMemo(() => {
		const imageUrl = isMobile
			? initialData?.smallBackgroundImageUrl
			: initialData?.largeBackgroundImageUrl;
		return `url("${imageUrl}")`;
	}, [initialData, isMobile]);

	useEffect(() => {
		const isMobileFunc = () => {
			setIsMobile(window.innerWidth <= SCREEN_WIDTHS.SM);
			setIsMobileXS(window.innerWidth <= SCREEN_WIDTHS.XS);
		};
		window.addEventListener("resize", isMobileFunc);
		return () => {
			window.removeEventListener("resize", isMobileFunc);
		};
	}, []);

	useEffect(() => {
		getSplashData("splashPageModal").then((initialData) => {
			if (initialData.status === "success") {
				setInitialData(initialData.data);
				setAudienceTypesData(initialData.data);
			}
		});
	}, []);

	useEffect(() => {
		if (step === audienceSortStepNames.initialStep) {
			setIsModalOpen(false);
		}
		if (
			step === audienceSortStepNames.initialStep ||
			step === audienceSortStepNames.countrySelection
		) {
			setCameFromInitialStep(true);
		} else if (step !== audienceSortStepNames.financialProfessionalAffirmation) {
			setCameFromInitialStep(false);
		}
	}, [step, isModalOpen]);

	const audienceSort = (
		<>
			{countryListData?.regions && step === audienceSortStepNames.countrySelection && (
				<CountrySelector
					currentPage={currentPage}
					setStep={setStep}
					hasCloseButton={true}
					isMobile={isMobile}
					countryListData={countryListData}
					setAudienceTypesData={setAudienceTypesData}
					cameFromInitialStep={cameFromInitialStep}
					setIsModalOpen={setIsModalOpen}
					audienceSortTheme={audienceSortTheme}
				/>
			)}
			{audienceTypesData && step === audienceSortStepNames.audienceTypeSelection && (
				<AudienceSelector
					currentPage={currentPage}
					audienceSortTheme={audienceSortTheme}
					isMobile={isMobile}
					audienceTypesData={audienceTypesData}
					countryListData={countryListData}
					setStep={setStep}
					setAudienceSelectionData={setAudienceSelectionData}
				/>
			)}

			{step === audienceSortStepNames.financialProfessionalAffirmation && (
				<ProfessionalAffirmation
					hasCloseButton={true}
					audienceSortTheme={audienceSortTheme}
					audienceSelectionData={audienceSelectionData}
					setStep={setStep}
					cameFromInitialStep={cameFromInitialStep}
					currentPage={currentPage}
					setIsModalOpen={setIsModalOpen}
				/>
			)}
		</>
	);

	const audienceSortMobile = (
		<>
			{step === audienceSortStepNames.audienceTypeSelection && (
				<AudienceSelectorMobile
					currentPage={currentPage}
					audienceSortTheme={audienceSortTheme}
					isMobile={isMobile}
					audienceTypesData={audienceTypesData}
					countryListData={countryListData}
					setStep={setStep}
					setAudienceSelectionData={setAudienceSelectionData}
					hasCloseButton={true}
				/>
			)}
			{step === audienceSortStepNames.countrySelection && (
				<CountrySelectorMobile
					currentPage={currentPage}
					countryListData={countryListData}
					hasCloseButton={true}
					setStep={setStep}
					setAudienceTypesData={setAudienceTypesData}
					audienceSortTheme={audienceSortTheme}
				/>
			)}
			{step === audienceSortStepNames.financialProfessionalAffirmation && (
				<ProfessionalAffirmationMobile
					audienceSelectionData={audienceSelectionData}
					currentPage={currentPage}
					hasHeader={true}
					hasCloseButton={true}
					setStep={setStep}
					cameFromInitialStep={cameFromInitialStep}
					audienceSortTheme={audienceSortTheme}
				/>
			)}
		</>
	);

	const locationChangeEventHandler = async (event) => {
		if (!countryListData) {
			await changeCountry(event, initialData, setCountryListData);
		}
		setStep("display-country-selection");
		setIsModalOpen(true);
	};

	return (
		<>
			<div className={"public-global-splash light-theme"} data-qa="public-global-splash">
				<SplashHeader
					globalSplashProps={{
						initialData,
						setCountryListData,
						setStep,
						navbarRef,
						setIsModalOpen,
						countryListData
					}}
				/>
				<div className="public-global-splash-content" style={{ backgroundImage: backgroundImage }}>
					{initialData && (
						<div className="public-global-splash-content-wrapper">
							{isMobileXS && (
								<LocationChangeCTA
									countryName={initialData.countryName}
									locationChangeText={initialData.locationChangeText}
									eventHandler={locationChangeEventHandler}
								/>
							)}
							<div className="public-global-splash-left">
								<div className="accent-bar"></div>
								<div className="public-global-splash-welcome">
									<span
										className="public-global-splash-greeting t-title-sm-german"
										dangerouslySetInnerHTML={{ __html: sanitizeHtml(initialData.greeting) }}
									/>
								</div>
							</div>

							<div className="public-global-splash-right">
								<AudienceSelectorGlobalSplash
									initialData={initialData}
									introduction={initialData.introduction}
									setStep={setStep}
									setIsModalOpen={setIsModalOpen}
									setCameFromInitialStep={setCameFromInitialStep}
									audienceTypesData={initialData}
									setAudienceSelectionData={setAudienceSelectionData}
									currentPage={currentPage}
									step={step}
								/>
							</div>
						</div>
					)}
				</div>
			</div>
			{!isMobile && isModalOpen && (
				<AudienceSortModal
					theme={audienceSortTheme}
					currentPage={currentPage}
					currentStep={step}
					setStep={setStep}
					isModalOpen={isModalOpen}
					setIsModalOpen={setIsModalOpen}
					isMobile={isMobile}
					className="audience-sort-modal"
				>
					{audienceSort}
				</AudienceSortModal>
			)}
			{isMobile && isModalOpen && step !== audienceSortStepNames.initialStep && (
				<AudienceSortModal
					theme={audienceSortTheme}
					currentPage={currentPage}
					currentStep={step}
					setStep={setStep}
					isModalOpen={isModalOpen}
					setIsModalOpen={setIsModalOpen}
					isMobile={isMobile}
					isWide={false}
					className="public-global-splash-mobile-modal"
				>
					{audienceSortMobile}
				</AudienceSortModal>
			)}
		</>
	);
};

export default GlobalSplashPage;
